/**
 * Tool Filters Component
 * Search and filter controls for the tools marketplace
 */

import { useState, useEffect } from "react";
import { cn } from "@utils/cn";
import type { LibraryDifficulty } from "@/types/models";
import type { PricingTier, IntegrationType } from "@/types/tools";

interface ToolFiltersProps {
  searchQuery: string;
  selectedDifficulty: LibraryDifficulty | "all";
  selectedPricingTier?: PricingTier | "all";
  selectedIntegrationType?: IntegrationType | "all";
  showBookmarkedOnly: boolean;
  onSearchChange: (query: string) => void;
  onDifficultyChange: (difficulty: LibraryDifficulty | "all") => void;
  onPricingTierChange?: (tier: PricingTier | "all") => void;
  onIntegrationTypeChange?: (type: IntegrationType | "all") => void;
  onBookmarkedChange: (showBookmarked: boolean) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  /** If true, shows marketplace-specific filters (pricing, integration type) */
  showMarketplaceFilters?: boolean;
}

// Difficulty options
const difficultyOptions: { value: LibraryDifficulty | "all"; label: string }[] =
  [
    { value: "all", label: "All Levels" },
    { value: "beginner", label: "Beginner" },
    { value: "intermediate", label: "Intermediate" },
    { value: "advanced", label: "Advanced" },
  ];

// Pricing tier options
const pricingTierOptions: { value: PricingTier | "all"; label: string }[] = [
  { value: "all", label: "All Pricing" },
  { value: "free", label: "Free" },
  { value: "freemium", label: "Freemium" },
  { value: "paid", label: "Paid" },
  { value: "enterprise", label: "Enterprise" },
];

// Integration type options
const integrationTypeOptions: {
  value: IntegrationType | "all";
  label: string;
}[] = [
  { value: "all", label: "All Types" },
  { value: "api", label: "API" },
  { value: "sdk", label: "SDK" },
  { value: "cli", label: "CLI" },
  { value: "standalone", label: "Standalone" },
  { value: "browser_extension", label: "Extension" },
  { value: "saas", label: "SaaS" },
];

const SEARCH_DEBOUNCE_MS = 300;

export function ToolFilters({
  searchQuery,
  selectedDifficulty,
  selectedPricingTier = "all",
  selectedIntegrationType = "all",
  showBookmarkedOnly,
  onSearchChange,
  onDifficultyChange,
  onPricingTierChange,
  onIntegrationTypeChange,
  onBookmarkedChange,
  onClearFilters,
  hasActiveFilters,
  showMarketplaceFilters = false,
}: ToolFiltersProps) {
  // Local state for search input to enable debouncing
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Sync local state when external searchQuery changes (e.g., URL navigation, clear filters)
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  // Debounce search input - only call onSearchChange after user stops typing
  useEffect(() => {
    // Skip if local value matches external value (already synced)
    if (localSearchQuery === searchQuery) {
      return;
    }

    const timer = setTimeout(() => {
      onSearchChange(localSearchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [localSearchQuery, searchQuery, onSearchChange]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search input */}
      <div className="relative flex-grow max-w-md">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          placeholder="Search tools..."
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
        />
        {localSearchQuery && (
          <button
            onClick={() => setLocalSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Difficulty dropdown */}
      <div className="relative">
        <select
          value={selectedDifficulty}
          onChange={(e) =>
            onDifficultyChange(e.target.value as LibraryDifficulty | "all")
          }
          className={cn(
            "appearance-none pl-3 pr-8 py-2.5 text-sm font-medium rounded-lg border transition-colors cursor-pointer",
            selectedDifficulty !== "all"
              ? "bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300"
              : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500",
          )}
        >
          {difficultyOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <svg
          className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Pricing tier dropdown (marketplace only) */}
      {showMarketplaceFilters && onPricingTierChange && (
        <div className="relative">
          <select
            value={selectedPricingTier}
            onChange={(e) =>
              onPricingTierChange(e.target.value as PricingTier | "all")
            }
            className={cn(
              "appearance-none pl-3 pr-8 py-2.5 text-sm font-medium rounded-lg border transition-colors cursor-pointer",
              selectedPricingTier !== "all"
                ? "bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500",
            )}
          >
            {pricingTierOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <svg
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      )}

      {/* Integration type dropdown (marketplace only) */}
      {showMarketplaceFilters && onIntegrationTypeChange && (
        <div className="relative">
          <select
            value={selectedIntegrationType}
            onChange={(e) =>
              onIntegrationTypeChange(e.target.value as IntegrationType | "all")
            }
            className={cn(
              "appearance-none pl-3 pr-8 py-2.5 text-sm font-medium rounded-lg border transition-colors cursor-pointer",
              selectedIntegrationType !== "all"
                ? "bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500",
            )}
          >
            {integrationTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <svg
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      )}

      {/* Bookmarks toggle */}
      <button
        onClick={() => onBookmarkedChange(!showBookmarkedOnly)}
        className={cn(
          "flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg border transition-colors",
          showBookmarkedOnly
            ? "bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300"
            : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500",
        )}
      >
        <svg
          className={cn("w-4 h-4", showBookmarkedOnly && "fill-current")}
          viewBox="0 0 24 24"
          fill={showBookmarkedOnly ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M5 2h14a1 1 0 011 1v19.143a.5.5 0 01-.766.424L12 18.03l-7.234 4.536A.5.5 0 014 22.143V3a1 1 0 011-1z" />
        </svg>
        Saved
      </button>

      {/* Clear filters button */}
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          className="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Clear
        </button>
      )}
    </div>
  );
}
